import { useEffect, useMemo, useRef, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Timer, Check, X, Barbell, Stop, Spinner, CaretLeft, Plus } from "@phosphor-icons/react";
import { useWeightUnit } from "@/lib/units";
import ExerciseDemoButton from "@/components/ExerciseDemoButton";
import ProgressionCoach from "@/components/ProgressionCoach";

function formatTime(totalSeconds) {
  const h = Math.floor(totalSeconds / 3600);
  const m = Math.floor((totalSeconds % 3600) / 60);
  const s = totalSeconds % 60;
  const pad = (n) => String(n).padStart(2, "0");
  return h > 0 ? `${pad(h)}:${pad(m)}:${pad(s)}` : `${pad(m)}:${pad(s)}`;
}

export default function WorkoutSession({ api, template, scheduleEntryId, onFinish, onCancel }) {
  const { unit: weightUnit, fromKg, toKg, label: weightLabel, formatWeight } = useWeightUnit();
  const startRef = useRef(Date.now());
  const [elapsed, setElapsed] = useState(0);
  const [saving, setSaving] = useState(false);
  const [autoRest, setAutoRest] = useState(() => {
    try { return localStorage.getItem("uptop-auto-rest") !== "0"; } catch { return true; }
  });

  useEffect(() => {
    try { localStorage.setItem("uptop-auto-rest", autoRest ? "1" : "0"); } catch { /* ignore */ }
  }, [autoRest]);

  // Parse a rest period in seconds from exercise notes. Default 90s.
  const parseRestSeconds = (notes) => {
    if (!notes) return 90;
    const text = String(notes).toLowerCase();
    // Match patterns like "rest 2-3 min", "60-90 sec", "rest 90s", "rest 2 min"
    const m = text.match(/(\d+)\s*(?:-\s*(\d+))?\s*(min|m\b|sec|s\b)/);
    if (!m) return 90;
    const lower = parseInt(m[1], 10);
    const upper = m[2] ? parseInt(m[2], 10) : null;
    const unit = m[3];
    const value = lower;
    const mult = unit.startsWith("m") ? 60 : 1;
    const secs = value * mult;
    // For ranges, take a midpoint
    if (upper) {
      const midSec = Math.round(((lower + upper) / 2) * mult);
      return Math.max(15, Math.min(600, midSec));
    }
    return Math.max(15, Math.min(600, secs));
  };

  // Deep-copy exercises so we can mutate set states freely.
  // Ensure every exercise has at least one set so the user always sees set rows.
  const [exercises, setExercises] = useState(() =>
    (template?.exercises || []).map((ex) => ({
      name: ex.name,
      notes: ex.notes || "",
      sets: (ex.sets && ex.sets.length > 0
        ? ex.sets
        : [{ reps: 0, weight: 0, completed: false }]
      ).map((s) => ({
        reps: s.reps || 0,
        weight: s.weight || 0,
        completed: false,
      })),
    }))
  );

  // Live timer
  useEffect(() => {
    const id = setInterval(() => {
      setElapsed(Math.floor((Date.now() - startRef.current) / 1000));
    }, 1000);
    return () => clearInterval(id);
  }, []);

  // Track refs to set rows so we can scroll/focus the next incomplete one when rest ends
  const setRefs = useRef({});
  const registerSetRef = (exIdx, setIdx, el) => {
    setRefs.current[`${exIdx}-${setIdx}`] = el;
  };

  // Listen for rest-done event → smooth-scroll + flash highlight the next incomplete set
  useEffect(() => {
    const handler = () => {
      let target = null;
      for (let i = 0; i < exercises.length; i++) {
        for (let j = 0; j < exercises[i].sets.length; j++) {
          if (!exercises[i].sets[j].completed) {
            target = setRefs.current[`${i}-${j}`];
            if (target) break;
          }
        }
        if (target) break;
      }
      if (!target) return;
      target.scrollIntoView({ behavior: "smooth", block: "center" });
      target.classList.add("session-set-flash");
      window.setTimeout(() => target.classList.remove("session-set-flash"), 1600);
    };
    window.addEventListener("uptop:rest-done", handler);
    return () => window.removeEventListener("uptop:rest-done", handler);
  }, [exercises]);

  const exerciseComplete = (ex) => ex.sets.length > 0 && ex.sets.every((s) => s.completed);
  const doneExCount = exercises.filter(exerciseComplete).length;

  const toggleSet = (exIdx, setIdx) => {
    setExercises((arr) => {
      const next = arr.map((ex, i) =>
        i !== exIdx
          ? ex
          : {
              ...ex,
              sets: ex.sets.map((s, j) =>
                j !== setIdx ? s : { ...s, completed: !s.completed }
              ),
            }
      );
      // If we just completed a set (was false, now true), start auto rest
      const before = arr[exIdx]?.sets?.[setIdx]?.completed;
      const after = next[exIdx]?.sets?.[setIdx]?.completed;
      if (autoRest && !before && after) {
        // Don't fire rest after the very last set of the last exercise (workout is done)
        const isLastSet = setIdx === next[exIdx].sets.length - 1;
        const isLastEx = exIdx === next.length - 1;
        const allDone = next.every((e) => e.sets.length > 0 && e.sets.every((s) => s.completed));
        if (!(allDone && isLastSet && isLastEx)) {
          const secs = parseRestSeconds(next[exIdx].notes);
          try {
            window.dispatchEvent(new CustomEvent("uptop:start-rest", { detail: { seconds: secs } }));
          } catch { /* ignore */ }
        }
      }
      return next;
    });
  };

  const updateSetField = (exIdx, setIdx, field, value) => {
    setExercises((arr) =>
      arr.map((ex, i) =>
        i !== exIdx
          ? ex
          : {
              ...ex,
              sets: ex.sets.map((s, j) =>
                j !== setIdx ? s : { ...s, [field]: value }
              ),
            }
      )
    );
  };

  const addSet = (exIdx) => {
    setExercises((arr) =>
      arr.map((ex, i) => {
        if (i !== exIdx) return ex;
        const last = ex.sets[ex.sets.length - 1] || { reps: 0, weight: 0 };
        return {
          ...ex,
          sets: [...ex.sets, { reps: last.reps, weight: last.weight, completed: false }],
        };
      })
    );
  };

  const removeSet = (exIdx, setIdx) => {
    setExercises((arr) =>
      arr.map((ex, i) =>
        i !== exIdx
          ? ex
          : { ...ex, sets: ex.sets.filter((_, j) => j !== setIdx) }
      )
    );
  };

  // Apply the progression coach suggestion to ALL incomplete sets of this exercise
  // (so the user only has to confirm reps/weight once instead of per row).
  const applyProgression = (exIdx, weightKg, reps) => {
    setExercises((arr) =>
      arr.map((ex, i) =>
        i !== exIdx
          ? ex
          : {
              ...ex,
              sets: ex.sets.map((s) =>
                s.completed ? s : { ...s, weight: Number(weightKg) || 0, reps: Number(reps) || 0 }
              ),
            }
      )
    );
    toast.success("Suggestion applied to remaining sets");
  };


  const totalCompletedVolume = useMemo(() => {
    return exercises.reduce(
      (sum, ex) =>
        sum +
        ex.sets.reduce(
          (s, set) => s + (set.completed ? (set.weight || 0) * (set.reps || 0) : 0),
          0
        ),
      0
    );
  }, [exercises]);

  const allDone = exercises.length > 0 && exercises.every(exerciseComplete);

  const stopAndSave = async () => {
    if (saving) return;
    setSaving(true);
    try {
      const durationMinutes = Math.max(1, Math.round(elapsed / 60));
      const payloadExercises = exercises.map((ex) => ({
        name: ex.name,
        sets: ex.sets.map((s) => ({
          reps: parseInt(s.reps) || 0,
          weight: parseFloat(s.weight) || 0,
          completed: !!s.completed,
        })),
        notes: ex.notes || "",
      }));

      await axios.post(`${api}/workouts`, {
        name: template?.name || "Workout",
        date: new Date().toISOString().slice(0, 10),
        duration_minutes: durationMinutes,
        exercises: payloadExercises,
        notes: scheduleEntryId ? "Started from scheduled session" : "",
      });

      if (scheduleEntryId) {
        try {
          await axios.put(`${api}/schedule/${scheduleEntryId}`, { completed: true });
        } catch (e) { /* non-fatal */ }
      }
      toast.success("Workout saved to your log!");
      onFinish?.();
    } catch (e) {
      console.error(e);
      toast.error("Failed to save workout");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="min-h-[calc(100vh-100px)] flex flex-col" data-testid="workout-session">
      {/* Timer header */}
      <div className="bg-gradient-to-r from-[#E05A1D]/15 to-transparent border border-[#E05A1D]/40 p-5 mb-4 flex items-center gap-4" data-testid="session-header">
        <button
          onClick={onCancel}
          className="text-[#A1A1AA] hover:text-white transition-colors p-1"
          title="Back"
          data-testid="session-back-btn"
        >
          <CaretLeft size={20} weight="bold" />
        </button>
        <div className="flex-1 min-w-0">
          <p className="text-[10px] font-bold tracking-[0.1em] uppercase text-[#E05A1D]">In Session</p>
          <h2
            className="text-xl sm:text-2xl font-bold tracking-tight uppercase text-white truncate"
            style={{ fontFamily: "'Barlow Condensed', sans-serif" }}
            data-testid="session-title"
          >
            {template?.name || "Workout"}
          </h2>
          <div className="flex items-center gap-3 mt-0.5 flex-wrap">
            <p className="text-[10px] uppercase tracking-wider text-[#A1A1AA]">
              {doneExCount}/{exercises.length} exercises · {formatWeight(totalCompletedVolume, { decimals: 0 })} lifted
            </p>
            <button
              onClick={() => setAutoRest((v) => !v)}
              className={`text-[10px] font-bold uppercase tracking-wider flex items-center gap-1 px-2 py-0.5 border transition-colors ${
                autoRest
                  ? "border-[#E05A1D] text-[#E05A1D] bg-[#E05A1D]/10"
                  : "border-[#27272A] text-[#71717A] hover:text-[#A1A1AA]"
              }`}
              title={autoRest ? "Auto rest is on" : "Auto rest is off"}
              data-testid="session-auto-rest-toggle"
            >
              <Timer size={10} weight="fill" /> Auto-Rest {autoRest ? "ON" : "OFF"}
            </button>
          </div>
        </div>
        <div className="text-right">
          <p className="text-[10px] font-bold tracking-[0.1em] uppercase text-[#71717A] flex items-center gap-1 justify-end">
            <Timer size={11} weight="fill" className="text-[#E05A1D]" /> Elapsed
          </p>
          <p
            className="text-3xl sm:text-4xl font-black text-white tabular-nums"
            style={{ fontFamily: "'Barlow Condensed', sans-serif" }}
            data-testid="session-timer"
          >
            {formatTime(elapsed)}
          </p>
        </div>
      </div>

      {/* Exercise list */}
      {exercises.length === 0 ? (
        <div className="flex-1 flex items-center justify-center border border-dashed border-[#27272A] p-10 text-center" data-testid="session-empty">
          <div>
            <Barbell size={36} className="text-[#27272A] mx-auto mb-3" />
            <p className="text-sm text-[#71717A]">This workout has no exercises. Stop to save it anyway, or go back.</p>
          </div>
        </div>
      ) : (
        <div className="flex-1 space-y-3" data-testid="session-exercises">
          {exercises.map((ex, exIdx) => {
            const isDone = exerciseComplete(ex);
            const setsDone = ex.sets.filter((s) => s.completed).length;
            return (
              <div
                key={exIdx}
                className={`border p-4 transition-all ${
                  isDone
                    ? "bg-[#0F0F0F] border-[#22C55E]/40"
                    : "bg-[#161616] border-[#27272A]"
                }`}
                data-testid={`session-exercise-${exIdx}`}
              >
                <div className="flex items-start gap-3 mb-3">
                  <div className="text-2xl sm:text-3xl font-black text-[#3f3f46] tabular-nums w-10 shrink-0" style={{ fontFamily: "'Barlow Condensed', sans-serif" }}>
                    {String(exIdx + 1).padStart(2, "0")}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <p className={`font-semibold ${isDone ? "text-[#22C55E] line-through" : "text-white"}`} data-testid={`session-exercise-name-${exIdx}`}>
                        {ex.name}
                      </p>
                      {ex.name && <ExerciseDemoButton api={api} name={ex.name} />}
                    </div>
                    <div className="flex items-center gap-2 mt-1 flex-wrap">
                      <span className={`text-[10px] font-bold uppercase tracking-wider ${isDone ? "text-[#22C55E]" : "text-[#A1A1AA]"}`}>
                        {setsDone}/{ex.sets.length} sets done
                      </span>
                      {ex.notes && (
                        <span className="text-[10px] text-[#71717A] italic truncate">· {ex.notes}</span>
                      )}
                    </div>
                  </div>
                </div>

                {/* Progressive overload coach — only when not all sets are completed */}
                {!isDone && ex.name && (
                  <div className="mb-3" data-testid={`session-coach-wrap-${exIdx}`}>
                    <ProgressionCoach
                      api={api}
                      exerciseName={ex.name}
                      onApply={({ weightKg, reps }) => applyProgression(exIdx, weightKg, reps)}
                    />
                  </div>
                )}

                {/* Sets */}
                <div className="space-y-1.5" data-testid={`session-sets-${exIdx}`}>
                  {ex.sets.map((set, setIdx) => (
                    <SetRow
                      key={setIdx}
                      exIdx={exIdx}
                      setIdx={setIdx}
                      set={set}
                      isExerciseDone={isDone}
                      registerRef={(el) => registerSetRef(exIdx, setIdx, el)}
                      onToggle={() => toggleSet(exIdx, setIdx)}
                      onChange={(field, value) => updateSetField(exIdx, setIdx, field, value)}
                      onRemove={ex.sets.length > 1 ? () => removeSet(exIdx, setIdx) : null}
                    />
                  ))}
                  <button
                    onClick={() => addSet(exIdx)}
                    className="text-[10px] text-[#71717A] hover:text-[#E05A1D] font-bold uppercase tracking-wider flex items-center gap-1 transition-colors mt-1"
                    data-testid={`session-add-set-${exIdx}`}
                  >
                    <Plus size={10} weight="bold" /> Add set
                  </button>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Stop button */}
      <div className="sticky bottom-0 bg-gradient-to-t from-[#0F0F0F] via-[#0F0F0F] to-transparent pt-6 pb-2 mt-4" data-testid="session-footer">
        <button
          onClick={stopAndSave}
          disabled={saving}
          className={`w-full font-black uppercase tracking-wide py-4 text-base flex items-center justify-center gap-2 transition-all ${
            allDone
              ? "bg-[#22C55E] hover:bg-[#16A34A] text-white"
              : "bg-[#E05A1D] hover:bg-[#F06B2E] text-white"
          } disabled:opacity-50`}
          style={{ fontFamily: "'Barlow Condensed', sans-serif" }}
          data-testid="session-stop-btn"
        >
          {saving ? (
            <><Spinner size={18} className="animate-spin" /> Saving…</>
          ) : (
            <><Stop size={18} weight="fill" /> Stop & Save Workout</>
          )}
        </button>
        <button
          onClick={onCancel}
          disabled={saving}
          className="w-full mt-2 text-xs text-[#71717A] hover:text-white font-bold uppercase tracking-wider py-2 flex items-center justify-center gap-1.5 transition-colors"
          data-testid="session-cancel-btn"
        >
          <X size={12} weight="bold" /> Cancel session
        </button>
      </div>
    </div>
  );
}

function SetRow({ exIdx, setIdx, set, isExerciseDone, onToggle, onChange, onRemove, registerRef }) {
  const { unit: weightUnit, fromKg, toKg, label: weightLabel } = useWeightUnit();
  const displayWeight = set.weight ? (weightUnit === "lb" ? Number(fromKg(set.weight).toFixed(1)) : set.weight) : "";
  return (
    <div
      ref={registerRef}
      className={`grid grid-cols-[42px_1fr_1fr_auto] gap-2 items-center px-2 py-1.5 border transition-all ${
        set.completed
          ? "bg-[#22C55E]/5 border-[#22C55E]/30 opacity-70"
          : "bg-[#0F0F0F] border-[#27272A]"
      }`}
      data-testid={`session-set-${exIdx}-${setIdx}`}
    >
      <span className={`text-[10px] font-bold tracking-[0.1em] uppercase ${set.completed ? "text-[#22C55E]" : "text-[#71717A]"}`}>
        Set {setIdx + 1}
      </span>
      <div className="flex items-center gap-1.5">
        <input
          type="number"
          min="0"
          value={set.reps || ""}
          onChange={(e) => onChange("reps", e.target.value)}
          disabled={set.completed}
          placeholder="reps"
          className={`w-full bg-[#161616] border border-[#27272A] text-sm px-2 py-1 text-center focus:border-[#E05A1D] outline-none tabular-nums ${
            set.completed ? "text-[#22C55E] line-through" : "text-white"
          } disabled:cursor-not-allowed`}
          data-testid={`session-set-reps-${exIdx}-${setIdx}`}
        />
        <span className="text-[10px] text-[#71717A]">reps</span>
      </div>
      <div className="flex items-center gap-1.5">
        <input
          type="number"
          min="0"
          step="0.5"
          value={displayWeight}
          onChange={(e) => {
            const v = parseFloat(e.target.value) || 0;
            const kg = weightUnit === "lb" ? Number(toKg(v).toFixed(2)) : v;
            onChange("weight", kg);
          }}
          disabled={set.completed}
          placeholder={weightLabel}
          className={`w-full bg-[#161616] border border-[#27272A] text-sm px-2 py-1 text-center focus:border-[#E05A1D] outline-none tabular-nums ${
            set.completed ? "text-[#22C55E] line-through" : "text-white"
          } disabled:cursor-not-allowed`}
          data-testid={`session-set-weight-${exIdx}-${setIdx}`}
        />
        <span className="text-[10px] text-[#71717A]">{weightLabel}</span>
      </div>
      <div className="flex items-center gap-1">
        <button
          onClick={onToggle}
          className={`flex items-center justify-center w-8 h-8 border transition-all ${
            set.completed
              ? "bg-[#22C55E] border-[#22C55E] text-white"
              : "bg-transparent border-[#27272A] text-[#A1A1AA] hover:border-[#22C55E] hover:text-[#22C55E]"
          }`}
          title={set.completed ? "Undo set" : "Mark set done"}
          data-testid={`session-set-toggle-${exIdx}-${setIdx}`}
        >
          <Check size={14} weight="bold" />
        </button>
        {onRemove && !set.completed && (
          <button
            onClick={onRemove}
            className="text-[#71717A] hover:text-red-500 transition-colors"
            title="Remove set"
            data-testid={`session-set-remove-${exIdx}-${setIdx}`}
          >
            <X size={12} weight="bold" />
          </button>
        )}
      </div>
    </div>
  );
}
